/**
 * Where the book's own files (page scans, QR overlays, audio) actually live.
 *
 * The content JSON stores them as `/assets/…`, which only resolves correctly
 * while the page and the files share an origin. That stops being true in the
 * two places this project is actually headed:
 *
 *   - mounted into Tilda, the script runs on bureauinsight.com while the files
 *     sit on the asset host, so `/assets/…` would be looked up on Tilda and
 *     404 on every page of the book;
 *   - served from an object store, the bundle lives under a bucket prefix
 *     rather than at the host root, so the leading slash points above it.
 *
 * Rewriting the JSON would only move the assumption; resolving at runtime
 * against wherever the bundle was loaded from keeps one layout working
 * everywhere. Fully-qualified URLs and `data:` are passed through untouched,
 * so content is free to point somewhere else per item.
 */

let base = null;
let version = null;

export function setAssetBase(url) {
  base = url || null;
}

/**
 * Release tag appended to every resolved asset URL as `?v=`. The bundle's file
 * names never change between releases (D-021 keeps them stable in the bucket),
 * and the bucket sends no Cache-Control, so browsers cache heuristically — a
 * returning reader could hold last release's content JSON against this
 * release's images, and the hotspot cut-outs then render at the old rects'
 * proportions (visibly squashed). The Tilda block passes its own `?v=` version
 * via `data-version`, so JSON, images and script move between releases as one.
 */
export function setAssetVersion(v) {
  version = typeof v === 'string' && v ? v : null;
}

export function versionedUrl(href) {
  if (!version || typeof href !== 'string' || !href) return href;
  try {
    const url = new URL(href);
    if (!url.searchParams.has('v')) url.searchParams.set('v', version);
    return url.href;
  } catch {
    return href;
  }
}

export function assetUrl(path) {
  if (typeof path !== 'string' || !path) return path;
  // Anything already carrying a scheme (http:, data:, blob:) is absolute.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
  if (!base) return path;
  // The stored form is root-relative; drop the leading slash so it resolves
  // inside the base rather than replacing its path.
  return versionedUrl(new URL(path.replace(/^\/+/, ''), base).href);
}
